import { defineStore } from 'pinia';
import type { Modals } from '~/interfaces/dto/general/modals';
import type { ISitemapRemap } from '~/interfaces/dto/sitemap/response';
import type {
  FeatureFlag,
  IConfigResponse,
} from '~/interfaces/dto/config/response';
import {
  brandCountryDetails,
  brandCurrencies,
  defaultFeatureFlags,
  defaultRedirects,
  defaultSitemap,
  overrideFeatureFlags,
  partialRegDefaultTimers,
  temporaryFeatureFlags,
} from 'assets/data/config';
import { globalKeys } from 'assets/data/global-keys';
import { useEnvironment } from '~/composables/useStates/useEnvironment';
import type {
  ISiteStore,
  ISiteNotice,
} from '~/interfaces/stores/site.store.interface';

type Nav = 'top' | 'left' | 'footer';

export const useSiteStore = defineStore('site', {
  state: (): ISiteStore => {
    return {
      region: 'ZA',
      regionCode: 'JZA',
      environment: 'production',
      redirects: [],
      sitemapHost: 'www.jackpotcity.co.za',
      cmsHost: 'cms1.jpc.africa',
      sitemapMap: new Map(Object.entries(defaultSitemap)),
      config: null,
      loading: false,
      fullscreenLoading: false,
      displayNotifications: false,
      siteNotice: {
        heading: '',
        content: {
          type: 'message',
          data: null,
        },
        severity: 'error',
        enabled: false,
        timeout: 3000,
        position: 'br',
      },
      unreadNotificationsCount: 0,
      modalActive: false,
      currentModal: null,
      currentModalContext: {},
      featureFlags: new Map(Object.entries(defaultFeatureFlags)),
      webview: false,
      demoTimeout: null,
    };
  },
  actions: {
    toggleFeature({ feature }) {
      this[feature] = !this[feature];
      if (feature === 'displayNotifications') {
        localStorage.setItem('notificationsPanel', 'isOpen');
      }
    },
    explicitToggleFeature({
      feature,
      toggle,
    }: {
      feature: string;
      toggle: boolean;
    }) {
      this[feature] = toggle;
    },
    setSiteMap(sitemap: Partial<ISitemapRemap>[]) {
      for (const sm of sitemap) {
        const path = sm.Path.startsWith('/') ? sm.Path : `/${sm.Path}`;
        this.sitemapMap.set(path, sm);
      }
    },
    addToSiteMap(sitemapEntry: Partial<ISitemapRemap>) {
      this.sitemapMap.set(sitemapEntry.Path, sitemapEntry);
    },
    setSitemapHost(host: string) {
      this.sitemapHost = host;
    },
    setRedirects(redirects: { fromLink: string; toLink: string }[]) {
      this.redirects = [...redirects, ...defaultRedirects];
    },
    setConfig(config: IConfigResponse) {
      const environment = useEnvironment();
      if (
        environment.value.environment.localhost ||
        environment.value.environment.testHost
      ) {
        config.featureFlags.push(...temporaryFeatureFlags);
      }
      this.config = config;
      const featureFlags = config?.featureFlags;
      if (!!featureFlags && featureFlags?.length > 0) {
        this.setFeatureFlags(featureFlags);
      }
    },
    setFeatureFlags(featureFlags: FeatureFlag[]) {
      const config = useRuntimeConfig();
      featureFlags.forEach((ff) => {
        const features = ff.features || ff.Features;
        const ffKey = ff.key || ff.Key;
        features.forEach((featureFlag) => {
          const featureKey = featureFlag.key;
          const enabled = featureFlag.enabled;
          const key = `${ffKey}.${featureKey}`
            .toLowerCase()
            .trim()
            .replace('\t', '');
          this.setFeatureFlag(key, enabled);
        });
      });
      Object.entries(config.public).forEach(([key, val]) => {
        const isBoolean = /^true|false$/i.test(String(val));
        if (isBoolean)
          this.setFeatureFlag(
            `runtime.${key.toLocaleLowerCase()}`,
            /^true$/i.test(String(val)),
          );
      });

      const featureFlagOverrides = Object.entries(
        overrideFeatureFlags?.[this.regionCode] || {},
      );
      if (featureFlagOverrides)
        featureFlagOverrides.forEach(([key, val]) => {
          this.setFeatureFlag(key, val);
        });
    },
    setEnvironment(
      environment: 'development' | 'staging' | 'uat' | 'production' | string,
    ) {
      this.environment = environment;
    },
    setRegion(region: string) {
      const config = useRuntimeConfig();
      const { environment } = config.public;

      this.environment = environment;

      const r = region?.toUpperCase();

      this.region = r;
      this.regionCode = `J${r}`;
    },
    toggleSiteNotice({
      heading,
      severity,
      enabled,
      timeout,
      content,
    }: Partial<ISiteNotice>) {
      content.type = content?.type ?? 'message';
      this.siteNotice = {
        ...this.siteNotice,
        heading,
        severity,
        enabled,
        timeout,
        content,
      };
    },
    setUnreadNotificationsCount(element: number) {
      this.unreadNotificationsCount = element;
    },
    activateModal(
      activeModal: Modals | string,
      activeModalAdditionalProps: unknown = {},
    ) {
      this.currentModal = activeModal;
      this.currentModalContext = activeModalAdditionalProps;
      this.modalActive = true;
    },
    deactivateModal() {
      this.currentModal = null;
      this.modalActive = false;
    },
    setFeatureFlag(key: string, val: boolean) {
      this.featureFlags.set(key, val);
    },
    setWebview(isWebview: boolean) {
      this.webview = isWebview;
    },
    setDemoPlayTimeout() {
      const auth = useAuthStore();
      const rg = useRgStore();
      if (auth.isLoggedIn) {
        if (auth.getIsUserCompliant && !rg.accountLocked)
          this.demoTimeout = setTimeout(() => {
            this.activateModal('switchToRealPlayPrompt');
          }, this.getDemoPlayPromptTimeout);
      } else
        this.demoTimeout = setTimeout(() => {
          this.activateModal('demoPlayRegPrompt');
        }, this.getDemoPlayPromptTimeout);
    },
    clearDemoPlayTimeout() {
      clearTimeout(this.demoTimeout);
    },
  },
  getters: {
    featureDisplay(state) {
      return ({ feature }) => state[feature];
    },
    isLoading(state) {
      return state.loading;
    },
    isFullscreenLoading(state) {
      return state.fullscreenLoading;
    },
    getNav(state) {
      const sitemap = [...state.sitemapMap.values()];
      return ({ nav }: { nav: Nav }) =>
        sitemap?.filter((item) => {
          return item?.Navigation[nav];
        });
    },
    getRedirects(state) {
      return state.redirects;
    },
    getSitemapMap(state) {
      return state.sitemapMap;
    },
    getSitemapHost(state) {
      return state.sitemapHost;
    },
    getCmsHost(state) {
      return state.cmsHost;
    },
    getConfig(state) {
      return state.config;
    },
    getDemoPlayPromptTimeout(state): number {
      return (
        state.config?.devConfig?.demoPlayPromptTimeout ||
        globalKeys.navigationKeys.demoPlayTimeoutFallback
      );
    },
    getDialingCode(state) {
      return brandCountryDetails[state.regionCode].countryDialingCode;
    },
    getCurrencyCode(state) {
      return brandCurrencies[state.regionCode].currencyCode;
    },
    getCurrencySymbol(state) {
      return brandCurrencies[state.regionCode].currencySymbol;
    },
    getRegion(state) {
      return state.region;
    },
    getRegionCode(state) {
      return state.regionCode;
    },
    getEnvironment(state) {
      return state.environment;
    },
    getSiteNotice(state) {
      return state.siteNotice;
    },
    getUnreadNotificationsCount(state) {
      return state.unreadNotificationsCount;
    },
    getModalActive(state) {
      return state.modalActive;
    },
    getCurrentModal(state) {
      return state.currentModal;
    },
    getFeatureFlags(state) {
      return state.featureFlags;
    },
    isWebview(state) {
      return state.webview;
    },
    bannerDevConfig(state) {
      return (
        state.config?.devConfig?.banners || {
          enableFade: false,
          enableFullWidth: 'false',
          defaultAspectRatios: {
            desktop: '16/3',
            mobile: '355/104',
          },
          aspectRatiosByVertical: {
            Home: {
              desktop: '16/5',
              mobile: '344/247',
            },
          },
        }
      );
    },
    getPartialRegOTPTimeout(state) {
      return (
        state.config?.devConfig?.partialRegTimers?.otpTimeout ||
        partialRegDefaultTimers.otpTimeout
      );
    },
    getPartialRegTimeBeforeDeletion(state) {
      return (
        state.config?.devConfig?.partialRegTimers?.timeBeforeAccountDeletion ||
        partialRegDefaultTimers.timeBeforeAccountDeletion
      );
    },
  },
});
